<template>
  <div ref="popoverContainer" class="d-inline popover-container" @mouseenter="onMouseenter" @mouseleave="onMouseleave">
    <div ref="triggerElement" :class="triggerClass" @click="onClick" @blur="onBlur" @focus="onFocus">
      <slot />
    </div>
    <transition name="fade">
      <div v-show="visible" ref="popover" :class="['popover bs-popover-auto', popoverClass]" :style="popoverStyles">
        <div v-if="title || $slots['title']" class="popover-header">
          <slot name="title">{{ title }}</slot>
        </div>
        <div class="popover-body">
          <div ref="arrow" class="arrow"></div>
          <slot name="content">{{ content }}</slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { computePosition, autoUpdate, offset, autoPlacement, shift, arrow } from "@floating-ui/dom";

export default {
  props: {
    placement: {
      type: String,
      default: "bottom",
    },
    content: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    trigger: {
      type: String,
      default: "hover",
    },
    triggerClass: {
      type: String,
      default: "d-inline",
    },
    popoverClass: {
      type: [String, Array, Object],
      default: "",
    },
    popoverStyle: {
      type: [String, Object],
      default: "",
    },
    delay: {
      type: Object,
      default: () => ({ show: 0, hide: 0 }),
    },
    gap: {
      type: Number,
      default: 20, // Adjusted for better alignment with Bootstrap
    },
  },
  emits: ["click", "mouseenter", "mouseleave", "blur", "focus"],
  data() {
    return {
      popoverStyles: {},
      arrowStyles: {},
      cleanupAutoUpdate: null,
      showTimeoutId: null,
      hideTimeoutId: null,
      visible: false,
      currentElement: null,
    };
  },
  watch: {
    placement() {
      this.setTooltipPosition();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setTooltipPosition();
    });
  },
  beforeUnmount() {
    this.cleanupAutoUpdate && this.cleanupAutoUpdate();
  },
  methods: {
    showTooltip() {
      clearTimeout(this.hideTimeoutId);
      this.showTimeoutId = setTimeout(() => {
        this.visible = true;
        this.setTooltipPosition();
      }, this.delay.show);
    },
    hideTooltip() {
      clearTimeout(this.showTimeoutId);
      this.hideTimeoutId = setTimeout(() => {
        this.visible = false;
        if (this.cleanupAutoUpdate) {
          this.cleanupAutoUpdate();
          this.cleanupAutoUpdate = null;
        }
      }, this.delay.hide);
    },
    onClick() {
      if (this.trigger === "click") {
        if (this.visible) {
          this.hideTooltip();
        } else {
          this.showTooltip();
        }
      }

      this.$emit("click");
    },
    onMouseenter() {
      if (this.trigger === "hover") {
        this.showTooltip();
      }
      this.$emit("mouseenter");
    },
    onMouseleave(e) {
      if (this.trigger === "hover" && e.relatedTarget !== this.currentElement) {
        this.hideTooltip();
      }
      this.$emit("mouseleave");
    },
    onBlur() {
      if (this.trigger === "focus") {
        this.hideTooltip();
      }
      this.$emit("blur");
    },
    onFocus() {
      if (this.trigger === "focus") {
        this.showTooltip();
      }
      this.$emit("focus");
    },
    onMove(e) {
      this.currentElement = e.target;
    },
    setTooltipPosition() {
      const referenceElement = this.$refs.triggerElement;
      const floatingElement = this.$refs.popover;
      const arrowElement = this.$refs.arrow;

      if (!referenceElement || !floatingElement || !arrowElement) return;

      this.cleanupAutoUpdate && this.cleanupAutoUpdate();

      this.cleanupAutoUpdate = autoUpdate(referenceElement, floatingElement, () => {
        computePosition(referenceElement, floatingElement, {
          placement: this.placement,
          middleware: [offset(this.gap), autoPlacement(), shift({ padding: 10 }), arrow({ element: arrowElement, padding: 10 })],
        }).then(({ x, y, placement, middlewareData }) => {
          this.popoverStyles = {
            left: `${x}px`,
            top: `${y}px`,
            position: "absolute",
          };

          const { x: arrowX, y: arrowY } = middlewareData.arrow;

          const staticSide = {
            top: "bottom",
            right: "left",
            bottom: "top",
            left: "right",
          }[placement.split("-")[0]];

          // Remove any existing placement classes
          arrowElement.classList.remove("arrow-top", "arrow-right", "arrow-bottom", "arrow-left");

          // Add the new placement class
          arrowElement.classList.add(`arrow-${staticSide}`);

          Object.assign(arrowElement.style, {
            left: arrowX != null ? `${arrowX}px` : "",
            top: arrowY != null ? `${arrowY}px` : "",
            right: "",
            bottom: "",
          });
        });
      });
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s linear;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
